import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Alert as MuiAlert,
} from '@mui/material'
import { useEffect, useState, forwardRef } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { businessCheck } from '../../utils/utils'
import TerminalSettingsComponent from '../terminal/terminal/terminalSettingsComponent'
import { ErrorScreen, ShowNoRecordsFound, SnackBarHideDuration, SnackBarTransition } from '../../utils/ui_utils'
import { useFetchOutlet } from '../../modules/outlet/outletHooks'
import { useTerminalSettingsCreate, useTerminalSettingsFetch, useTerminalSettingsUpdate } from '../../modules/terminalSettings/terminalSettingsHooks'
import ListTypeShimmer from '../../utils/ui_components/shimmers/listTypeShimmer'
import _ from 'lodash'

function TerminalSettings() {
  const [selectedOutlet, setSelectedOutlet] = useState('')
  const [outlets, setOutlets] = useState([])
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)

  const { outletFetch, outletFetchData, outletFetchError, outletFetchLoading } = useFetchOutlet()
  const { terminalSettingsCreate } = useTerminalSettingsCreate()
  const { terminalSettingsFetch, terminalSettingsFetchData, terminalSettingsFetchError, terminalSettingsFetchLoading } = useTerminalSettingsFetch()
  const { terminalSettingsUpdate } = useTerminalSettingsUpdate()

  const defaultSettings = {
    roundoff: true,
    duplicate_bill_status: false,
    bill_save_status: false,
    gst_status: false,
    token_by_printer_num: false,
    open_price_status: false,
    duplicate_bill_status: false,
    bill_reset_status: true,
    kot_status: false,
    reverse_gst_status: false,
    hf_status: false,
    logo_status: false,
    waiter_status: false,
    kot_token_delete: false,
    guest_status: false,
    token_logo_status: false,
    kottoken_status: null,
    tokenhf_status: false,
    parcel_status: false,
    parcel_amount: 0,
    parcel_sgst: 0,
    parcel_cgst: 0,
    quantitywindow_status: false,
    servicecharge_status: false,
    servicecharge: 0,
    inventory_status: false,
    bill_without_stock_status: true,
    bill_settlement_status: false,
    udn_commission_status: 0,
  }

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const handleSnackbarClose = () => {
    setSnackBar(false)
  }

  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const history = useNavigate()

  const fetchRecords = () => {
    let payload = {
      pageNumber: 1,
      limit: 100
    }
    outletFetch(payload)
  }


  useEffect(() => {
    businessCheck(history)
    fetchRecords();
  }, [])

  useEffect(() => {
    if (outletFetchData.data?.length > 0) {
      setOutlets(outletFetchData.data);
      setSelectedOutlet(outletFetchData.data[0]?._id)
      terminalSettingsFetch(outletFetchData.data[0]?._id)
    }
    else {
      setOutlets([])
      setSelectedOutlet("")
    }
  }, [outletFetchData])

  const handleOutletChange = (event) => {
    setSelectedOutlet(event.target.value)
    fetchTerminalSettingsOnError(event.target.value)
  }

  const fetchOutletOnError = () => {
    fetchRecords();
  }

  const fetchTerminalSettingsOnError = (outlet_id) => {
    terminalSettingsFetch(outlet_id)
  }

  const updateTerminalSettings = (values,isBulkUpdate) => {
    if (values._id) {
      values.id = values._id
      delete values._id
    }
    delete values.business_id
    delete values.timestamp
    if (values.parcel_status == 1)
      values.parcel_status = true
    else if (values.parcel_status == 0)
      values.parcel_status = false
    else
      values.parcel_status = null

    if (values.kottoken_status == 1)
      values.kottoken_status = true
    else if (values.kottoken_status == 0)
      values.kottoken_status = false
    else
      values.kottoken_status = null
    const payload = {
      settings: values,
      bulk_settings_update: isBulkUpdate
    }
    terminalSettingsUpdate(payload, (success, error, res) => {
      if (error) {
        toastMessage(res, 'error')
      }
      else {
        toastMessage(res, 'success')
        terminalSettingsFetch(selectedOutlet)
      }
    }) 
  }
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Helmet title="Terminal Settings" />

      {/* Page header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Terminal Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Configure billing, KOT, GST and inventory behaviour for this outlet.
          </Typography>
        </Box>
        <Box sx={{ minWidth: { xs: '100%', md: 280 } }}>
          <TextField
            size="small"
            label="Outlet"
            id="outlet_id"
            name="outlet_id"
            variant="outlined"
            value={selectedOutlet}
            onChange={handleOutletChange}
            select
            fullWidth
            disabled={outletFetchLoading || outlets.length === 0}
          >
            {outlets.map((outlet) => (
              <MenuItem key={outlet._id} value={outlet._id}>
                {outlet.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Stack>
      <Divider sx={{ mb: 3 }} />

      {outletFetchError ? (
        <ErrorScreen onClick={fetchOutletOnError} />
      ) : terminalSettingsFetchError ? (
        <ErrorScreen onClick={() => fetchTerminalSettingsOnError(selectedOutlet)} />
      ) : (outletFetchLoading || terminalSettingsFetchLoading) ? (
        <ListTypeShimmer />
      ) : outlets.length <= 0 ? (
        <ShowNoRecordsFound />
      ) : selectedOutlet === '' ? null : !terminalSettingsFetchData ? (
        <Card variant="outlined" sx={{ borderRadius: 2, maxWidth: 560, mx: 'auto', mt: 4 }}>
          <CardContent>
            <Stack direction="column" spacing={2} sx={{ p: 4, alignItems: 'center', textAlign: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Terminal settings are not available for this outlet.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create default settings to get started, then customise as needed.
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  defaultSettings.outlet_id = selectedOutlet
                  defaultSettings.hsn = false
                  terminalSettingsCreate(defaultSettings, (success, error) => {
                    if (error) {
                      toastMessage('Default settings not created. Try again.', 'error')
                    } else {
                      toastMessage('Default settings created', 'success')
                      terminalSettingsFetch(selectedOutlet)
                    }
                  })
                }}
              >
                Create Default Settings
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <TerminalSettingsComponent
          initialValues={_.cloneDeep(terminalSettingsFetchData)}
          okText="Update"
          onUpdateAll={(values) => updateTerminalSettings(values, true)}
          onSubmit={(values) => updateTerminalSettings(values, false)}
        />
      )}

      <Snackbar
        open={snackbar}
        autoHideDuration={SnackBarHideDuration}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleSnackbarClose}
        TransitionComponent={SnackBarTransition}
      >
        <Alert onClose={handleSnackbarClose} severity={severity}>
          {PopUpMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
export default TerminalSettings
